import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Users,
  UsersRound,
  Inbox,
  Plus,
  Trash2,
  Loader2,
  Save,
  Pencil,
  Network,
} from 'lucide-react';
import { IconBrandHipchat, IconUser, IconVersions } from '@tabler/icons-react';
import ProcessConfigSkeleton from '../../process-config/ConfigStatusView2Skeleton';
import '../../process-config/ConfigStatusView2.css';
import '../../process-config/components/CondicoesIntervencaoTableV2.css';
import './CustomersChatView.css';
import { VchSinglePickerChip } from './VchSinglePickerChip';
import { VchUserPickerPanel } from './VchUserPickerPanel';
import { CustomersChatAgentsTeamsPanel } from './CustomersChatAgentsTeamsPanel';
import {
  fetchChatSettings,
  fetchChatAgents,
  fetchChatEligibleUsers,
  createChatAgent,
  deleteChatAgent,
  fetchChatTeams,
  createChatTeam,
  updateChatTeam,
  deleteChatTeam,
  assignChatTeamAgents,
  fetchChatTeamAgents,
  fetchChatInbox,
  updateChatInbox,
} from '../services/customersChatService';
import {
  CHAT_SECTIONS,
  DAY_LABELS,
  ROLE_LABELS,
  chatStatusHint,
  canUseChatAdmin,
  teamMemberIds,
  mergeWorkingHours,
  normalizeWorkingHourDay,
  buildInboxSettingsPayload,
} from '../utils/customersChatUtils';

const ROLE_PICKER_ITEMS = [
  { value: 'agent', label: 'Agente' },
  { value: 'administrator', label: 'Administrador' },
];

function VchPcs2Switch({ checked, onChange, ariaLabel, disabled, className = '' }) {
  const inputRef = useRef(null);
  return (
    <label
      className={`pcs2-switch${disabled ? ' pcs2-switch--disabled' : ''}${
        className ? ` ${className}` : ''
      }`}
    >
      <input
        ref={inputRef}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => {
          onChange(e);
          requestAnimationFrame(() => inputRef.current?.blur());
        }}
        aria-label={ariaLabel}
      />
      <span className="pcs2-switch-pill">
        <span className="pcs2-switch-dot" />
      </span>
    </label>
  );
}

function VchSection({ icon: Icon, title, desc, children, action }) {
  return (
    <section className="pcs2-section vch-section">
      <div className="pcs2-section-aside">
        <div className="pcs2-section-heading">
          <Icon size={16} strokeWidth={1.75} aria-hidden />
          <h3 className="pcs2-section-title">{title}</h3>
        </div>
        {desc ? <p className="pcs2-section-desc">{desc}</p> : null}
      </div>
      <div className="pcs2-section-body">
        {action ? <div className="vch-section-action">{action}</div> : null}
        {children}
      </div>
    </section>
  );
}

function AgentsPanel({ enabled }) {
  const [rows, setRows] = useState([]);
  const [eligible, setEligible] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eligibleLoading, setEligibleLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [role, setRole] = useState('agent');
  const [deletingId, setDeletingId] = useState(null);

  const loadEligible = useCallback(async () => {
    if (!enabled) return;
    setEligibleLoading(true);
    try {
      const j = await fetchChatEligibleUsers();
      const list = Array.isArray(j.data) ? j.data : [];
      setEligible(list);
      setSelectedUserId((cur) =>
        cur && list.some((u) => String(u.id) === String(cur)) ? cur : ''
      );
    } catch {
      setEligible([]);
      setSelectedUserId('');
    } finally {
      setEligibleLoading(false);
    }
  }, [enabled]);

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const j = await fetchChatAgents();
      setRows(Array.isArray(j.data) ? j.data : []);
    } catch (e) {
      setError(e.message || 'Erro ao carregar agentes');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    load();
    loadEligible();
  }, [load, loadEligible]);

  const handleCreate = async () => {
    if (!selectedUserId) {
      window.alert('Selecione um usuário do Verve.');
      return;
    }
    setSaving(true);
    try {
      await createChatAgent({
        verveUserId: Number(selectedUserId),
        role,
      });
      setSelectedUserId('');
      setRole('agent');
      await Promise.all([load(), loadEligible()]);
    } catch (e) {
      window.alert(e.message || 'Erro ao vincular agente');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (agent) => {
    const id = agent.id ?? agent.user_id;
    const label = agent.name || agent.email || 'agente';
    const hasCwId = id != null && Number(id) > 0;
    const hasFallback = Number(agent.linkId) > 0 || Number(agent.verveUserId) > 0;
    if (!hasCwId && !hasFallback) {
      window.alert('Não foi possível identificar o vínculo deste agente.');
      return;
    }
    if (!window.confirm(`Remover o agente "${label}"?`)) return;
    setDeletingId(hasCwId ? id : agent.linkId || agent.verveUserId);
    try {
      await deleteChatAgent(hasCwId ? id : 0, {
        linkId: agent.linkId,
        verveUserId: agent.verveUserId,
      });
      await Promise.all([load(), loadEligible()]);
    } catch (e) {
      window.alert(e.message || 'Erro ao remover');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <VchSection
      icon={Users}
      title="Agentes"
      desc="Vincule usuários internos do Verve ao atendimento do chat (nome e e-mail vêm do cadastro de usuários)."
      action={(
        <div className="vch-inline-form vch-filter-chips">
          <VchSinglePickerChip
            className="vch-filter-chips__user"
            chipLabel="Usuário"
            placeholderText="Clique e informe o usuário"
            icon={IconUser}
            items={eligible}
            getKey={(u) => String(u.id)}
            getLabel={(u) => u.displayName || u.email || u.name}
            selectedKey={selectedUserId}
            onSelect={setSelectedUserId}
            disabled={saving || eligibleLoading || eligible.length === 0}
            showSearch
            searchPlaceholder="Buscar usuário"
            popoverId="vch-agent-user"
            listAriaLabel="Usuário do Verve"
            emptyMessage={eligibleLoading ? 'Carregando…' : 'Nenhum usuário disponível'}
            matchAnchorWidth
            PanelComponent={VchUserPickerPanel}
            filterItem={(u, q) => {
              const fields = [u.displayName, u.loginEmail, u.name, u.email]
                .filter(Boolean)
                .map((s) => String(s).toLowerCase());
              return fields.some((f) => f.includes(q));
            }}
          />
          <div className="vch-filter-chips__actions">
            <VchSinglePickerChip
              chipLabel="Papel"
              icon={IconVersions}
              items={ROLE_PICKER_ITEMS}
              getKey={(r) => r.value}
              getLabel={(r) => r.label}
              selectedKey={role}
              onSelect={setRole}
              disabled={saving || !selectedUserId}
              showSearch={false}
              popoverId="vch-agent-role"
              listAriaLabel="Papel do agente"
              width={200}
              maxHeight={160}
            />
            <button
              type="button"
              className="vch-add-agent-btn"
              onClick={handleCreate}
              disabled={saving || !selectedUserId}
            >
              {saving ? <Loader2 size={13} className="vch-spin" /> : <Plus size={13} strokeWidth={2.5} />}
              Vincular
            </button>
          </div>
        </div>
      )}
    >
      {error && <p className="vch-error">{error}</p>}
      <div className="ci2-root vch-ci2">
        <div className="ci2-panel">
          <div className="ci2-table-scroll vch-ci2-scroll">
            <table className="ci2-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>E-mail</th>
                  <th>Usuário Verve</th>
                  <th>Papel</th>
                  <th className="vch-col-act" aria-label="Ações" />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="vch-empty">
                      <ProcessConfigSkeleton variant="tableBody" ariaLabel="Carregando agentes" />
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="vch-empty">
                      Nenhum agente vinculado.
                    </td>
                  </tr>
                ) : (
                  rows.map((a) => {
                    const id = a.id ?? a.user_id;
                    const rowKey = id != null && Number(id) > 0 ? id : `link-${a.linkId || a.verveUserId}`;
                    const deletingKey = id != null && Number(id) > 0 ? id : a.linkId || a.verveUserId;
                    return (
                      <tr key={rowKey}>
                        <td>{a.name || '—'}</td>
                        <td className="vch-mono">{a.email || '—'}</td>
                        <td className="vch-mono">
                          {a.verveUserId != null ? `#${a.verveUserId}` : '—'}
                        </td>
                        <td>{ROLE_LABELS[a.role] || a.role || '—'}</td>
                        <td className="vch-col-act">
                          <button
                            type="button"
                            className="vch-icon-btn vch-icon-btn--danger"
                            onClick={() => handleDelete(a)}
                            disabled={deletingId === deletingKey}
                            aria-label={`Remover ${a.name}`}
                          >
                            {deletingId === id ? (
                              <Loader2 size={14} className="vch-spin" />
                            ) : (
                              <Trash2 size={14} />
                            )}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </VchSection>
  );
}

function teamAllowAutoAssign(team) {
  return Boolean(team?.allowAutoAssign ?? team?.allow_auto_assign);
}

function TeamsPanel({ enabled, clientId }) {
  const [teams, setTeams] = useState([]);
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [allowAutoAssign, setAllowAutoAssign] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [assignTeamId, setAssignTeamId] = useState(null);
  const [selectedAgentIds, setSelectedAgentIds] = useState(() => new Set());
  const [assignSaving, setAssignSaving] = useState(false);
  const [editTeam, setEditTeam] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editAllowAutoAssign, setEditAllowAutoAssign] = useState(true);
  const [editSaving, setEditSaving] = useState(false);

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const j = await fetchChatTeams();
      setTeams(Array.isArray(j.data) ? j.data : []);
    } catch (e) {
      setError(e.message || 'Erro ao carregar times');
      setTeams([]);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  const loadAgents = useCallback(async () => {
    if (!enabled) return;
    setAgentsLoading(true);
    try {
      const j = await fetchChatAgents(clientId);
      const list = Array.isArray(j.data) ? j.data : [];
      setAgents(list.filter((a) => Number(a.id ?? a.user_id) > 0));
    } catch {
      setAgents([]);
    } finally {
      setAgentsLoading(false);
    }
  }, [enabled, clientId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    if (!name.trim()) {
      window.alert('Informe o nome do time.');
      return;
    }
    setCreating(true);
    try {
      await createChatTeam({
        name: name.trim(),
        description: description.trim() || undefined,
        allowAutoAssign,
      });
      setName('');
      setDescription('');
      setAllowAutoAssign(true);
      await load();
    } catch (e) {
      window.alert(e.message || 'Erro ao criar time');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (team) => {
    if (!window.confirm(`Remover o time "${team.name}"?`)) return;
    setDeletingId(team.id);
    try {
      await deleteChatTeam(team.id);
      if (assignTeamId === team.id) setAssignTeamId(null);
      if (editTeam?.id === team.id) setEditTeam(null);
      await load();
    } catch (e) {
      window.alert(e.message || 'Erro ao remover');
    } finally {
      setDeletingId(null);
    }
  };

  const openEdit = (team) => {
    setAssignTeamId(null);
    setEditTeam(team);
    setEditName(team.name || '');
    setEditDescription(team.description || '');
    setEditAllowAutoAssign(teamAllowAutoAssign(team));
  };

  const saveEdit = async () => {
    if (!editTeam) return;
    if (!editName.trim()) {
      window.alert('Informe o nome do time.');
      return;
    }
    setEditSaving(true);
    try {
      await updateChatTeam(editTeam.id, {
        name: editName.trim(),
        description: editDescription.trim(),
        allowAutoAssign: editAllowAutoAssign,
      });
      setEditTeam(null);
      await load();
    } catch (e) {
      window.alert(e.message || 'Erro ao atualizar time');
    } finally {
      setEditSaving(false);
    }
  };

  const openAssign = async (team) => {
    setEditTeam(null);
    setAssignTeamId(team.id);
    setSelectedAgentIds(new Set(teamMemberIds(team)));
    await loadAgents();
    try {
      const j = await fetchChatTeamAgents(team.id);
      const members = Array.isArray(j.data) ? j.data : [];
      const ids = members
        .map((m) => Number(m.id ?? m.user_id ?? m.agent_id))
        .filter((n) => Number.isFinite(n) && n > 0);
      setSelectedAgentIds(new Set(ids));
    } catch {
      setSelectedAgentIds(new Set(teamMemberIds(team)));
    }
  };

  const toggleAgent = (id) => {
    const nid = Number(id);
    setSelectedAgentIds((prev) => {
      const next = new Set(prev);
      if (next.has(nid)) next.delete(nid);
      else next.add(nid);
      return next;
    });
  };

  const saveAssign = async () => {
    if (!assignTeamId) return;
    setAssignSaving(true);
    try {
      const j = await assignChatTeamAgents(assignTeamId, [...selectedAgentIds]);
      const memberIds = Array.isArray(j.data?.memberIds)
        ? j.data.memberIds
        : [...selectedAgentIds];
      setSelectedAgentIds(
        new Set(memberIds.map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0))
      );
      await load();
    } catch (e) {
      window.alert(e.message || 'Erro ao atribuir agentes');
    } finally {
      setAssignSaving(false);
    }
  };

  return (
    <VchSection
      icon={UsersRound}
      title="Times / filas"
      desc="Organize o atendimento em filas e defina quais agentes atendem em cada uma."
      action={(
        <div className="vch-inline-form vch-team-create">
          <input
            className="vch-input"
            placeholder="Nome do time"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={creating}
          />
          <input
            className="vch-input vch-input--wide"
            placeholder="Descrição (opcional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={creating}
          />
          <label className="vch-check-row vch-check-row--inline">
            <input
              type="checkbox"
              checked={allowAutoAssign}
              onChange={(e) => setAllowAutoAssign(e.target.checked)}
              disabled={creating}
            />
            <span>Permitir atribuição automática para este time.</span>
          </label>
          <button
            type="button"
            className="vch-btn vch-btn--primary"
            onClick={handleCreate}
            disabled={creating}
          >
            {creating ? <Loader2 size={14} className="vch-spin" /> : <Plus size={14} />}
            Criar time
          </button>
        </div>
      )}
    >
      {error && <p className="vch-error">{error}</p>}
      <div className="ci2-root vch-ci2">
        <div className="ci2-panel">
          <div className="ci2-table-scroll vch-ci2-scroll">
            <table className="ci2-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Descrição</th>
                  <th>Agentes</th>
                  <th>Auto atribuição</th>
                  <th className="vch-col-act" aria-label="Ações" />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="vch-empty">
                      <ProcessConfigSkeleton variant="tableBody" ariaLabel="Carregando times" />
                    </td>
                  </tr>
                ) : teams.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="vch-empty">
                      Nenhum time cadastrado.
                    </td>
                  </tr>
                ) : (
                  teams.map((t) => (
                    <tr key={t.id}>
                      <td>{t.name}</td>
                      <td>{t.description || '—'}</td>
                      <td>{t.agentsCount != null ? t.agentsCount : teamMemberIds(t).length}</td>
                      <td>{teamAllowAutoAssign(t) ? 'Sim' : 'Não'}</td>
                      <td className="vch-col-act">
                        <div className="vch-row-actions">
                          <button
                            type="button"
                            className="vch-btn vch-btn--ghost"
                            onClick={() => openEdit(t)}
                          >
                            <Pencil size={13} strokeWidth={2} aria-hidden />
                            Editar
                          </button>
                          <button
                            type="button"
                            className="vch-btn vch-btn--ghost"
                            onClick={() => openAssign(t)}
                          >
                            Agentes
                          </button>
                          <button
                            type="button"
                            className="vch-icon-btn vch-icon-btn--danger"
                            onClick={() => handleDelete(t)}
                            disabled={deletingId === t.id}
                            aria-label={`Remover ${t.name}`}
                          >
                            {deletingId === t.id ? (
                              <Loader2 size={14} className="vch-spin" />
                            ) : (
                              <Trash2 size={14} />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {editTeam != null && (
        <div className="vch-assign-panel">
          <p className="vch-assign-title">Editar time</p>
          <div className="vch-inline-form vch-team-edit">
            <input
              className="vch-input"
              placeholder="Nome do time"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              disabled={editSaving}
            />
            <input
              className="vch-input vch-input--wide"
              placeholder="Descrição (opcional)"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              disabled={editSaving}
            />
            <label className="vch-check-row vch-check-row--inline">
              <input
                type="checkbox"
                checked={editAllowAutoAssign}
                onChange={(e) => setEditAllowAutoAssign(e.target.checked)}
                disabled={editSaving}
              />
              <span>Permitir atribuição automática para este time.</span>
            </label>
          </div>
          <div className="vch-assign-actions">
            <button
              type="button"
              className="vch-btn vch-btn--ghost"
              onClick={() => setEditTeam(null)}
              disabled={editSaving}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="vch-btn vch-btn--primary"
              onClick={saveEdit}
              disabled={editSaving}
            >
              {editSaving ? <Loader2 size={14} className="vch-spin" /> : <Save size={14} />}
              Salvar
            </button>
          </div>
        </div>
      )}

      {assignTeamId != null && (
        <div className="vch-assign-panel">
          <p className="vch-assign-title">Agentes do time</p>
          <p className="vch-assign-hint">
            Marcados = já vinculados. Desmarque e salve para remover; marque e salve para adicionar.
          </p>
          <div className="vch-assign-list">
            {agentsLoading ? (
              <ProcessConfigSkeleton variant="listBody" ariaLabel="Carregando agentes" />
            ) : agents.length === 0 ? (
              <p className="vch-empty-inline">Cadastre agentes primeiro.</p>
            ) : (
              agents.map((a) => {
                const id = Number(a.id ?? a.user_id);
                return (
                  <label key={id} className="vch-check-row">
                    <input
                      type="checkbox"
                      checked={selectedAgentIds.has(id)}
                      onChange={() => toggleAgent(id)}
                    />
                    <span>
                      {a.name} <span className="vch-muted">({a.email})</span>
                    </span>
                  </label>
                );
              })
            )}
          </div>
          <div className="vch-assign-actions">
            <button
              type="button"
              className="vch-btn vch-btn--ghost"
              onClick={() => setAssignTeamId(null)}
              disabled={assignSaving}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="vch-btn vch-btn--primary"
              onClick={saveAssign}
              disabled={assignSaving}
            >
              {assignSaving ? <Loader2 size={14} className="vch-spin" /> : <Save size={14} />}
              Salvar atribuição
            </button>
          </div>
        </div>
      )}
    </VchSection>
  );
}

function InboxPanel({ enabled }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedSnapshot, setSavedSnapshot] = useState('');
  const [greetingEnabled, setGreetingEnabled] = useState(false);
  const [greetingMessage, setGreetingMessage] = useState('');
  const [workingHoursEnabled, setWorkingHoursEnabled] = useState(false);
  const [outOfOfficeMessage, setOutOfOfficeMessage] = useState('');
  const [workingHours, setWorkingHours] = useState(() => mergeWorkingHours());
  const [blockOutsideHours, setBlockOutsideHours] = useState(false);
  const [groupConversationsByClient, setGroupConversationsByClient] = useState(false);

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const j = await fetchChatInbox();
      const inbox = j.data || {};
      const nextGreetingEnabled = Boolean(inbox.greeting_enabled);
      const nextGreetingMessage = String(inbox.greeting_message || '');
      const nextWorkingHoursEnabled = Boolean(inbox.working_hours_enabled);
      const nextOutOfOfficeMessage = String(inbox.out_of_office_message || '');
      const nextWorkingHours = mergeWorkingHours(inbox.working_hours);
      const nextBlockOutsideHours = Boolean(inbox.block_outside_hours);
      const nextGroupByClient = Boolean(inbox.group_conversations_by_client);
      setGreetingEnabled(nextGreetingEnabled);
      setGreetingMessage(nextGreetingMessage);
      setWorkingHoursEnabled(nextWorkingHoursEnabled);
      setOutOfOfficeMessage(nextOutOfOfficeMessage);
      setWorkingHours(nextWorkingHours);
      setBlockOutsideHours(nextBlockOutsideHours);
      setGroupConversationsByClient(nextGroupByClient);
      setSavedSnapshot(
        JSON.stringify(
          buildInboxSettingsPayload({
            greetingEnabled: nextGreetingEnabled,
            greetingMessage: nextGreetingMessage.trim(),
            workingHoursEnabled: nextWorkingHoursEnabled,
            outOfOfficeMessage: nextOutOfOfficeMessage.trim(),
            workingHours: nextWorkingHours,
            blockOutsideHours: nextBlockOutsideHours,
            groupConversationsByClient: nextGroupByClient,
          })
        )
      );
    } catch (e) {
      setError(e.message || 'Erro ao carregar inbox');
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    load();
  }, [load]);

  const updateDay = (day, patch) => {
    setWorkingHours((prev) =>
      prev.map((w) => {
        if (w.day_of_week !== day) return w;
        return normalizeWorkingHourDay({ ...w, ...patch }, day);
      })
    );
  };

  const currentSnapshot = useMemo(
    () =>
      JSON.stringify(
        buildInboxSettingsPayload({
          greetingEnabled,
          greetingMessage: greetingMessage.trim(),
          workingHoursEnabled,
          outOfOfficeMessage: outOfOfficeMessage.trim(),
          workingHours,
          blockOutsideHours,
          groupConversationsByClient,
        })
      ),
    [
      greetingEnabled,
      greetingMessage,
      workingHoursEnabled,
      outOfOfficeMessage,
      workingHours,
      blockOutsideHours,
      groupConversationsByClient,
    ]
  );

  const dirty = Boolean(savedSnapshot) && currentSnapshot !== savedSnapshot;

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateChatInbox(
        buildInboxSettingsPayload({
          greetingEnabled,
          greetingMessage: greetingMessage.trim(),
          workingHoursEnabled,
          outOfOfficeMessage: outOfOfficeMessage.trim(),
          workingHours,
          blockOutsideHours,
          groupConversationsByClient,
        })
      );
      await load();
    } catch (e) {
      window.alert(e.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <ProcessConfigSkeleton variant="settings" embedded ariaLabel="Carregando configurações do inbox" />;
  }

  return (
    <div className="vch-inbox-shell">
      <div className="vch-inbox-scroll">
        <div className="pcs2-page vch-inbox-page">
          {error && <p className="vch-error">{error}</p>}

          <VchSection
            icon={Inbox}
            title="Mensagem de saudação"
            desc="Exibida quando o cliente inicia uma conversa."
          >
        <div className="pcs2-setting-row">
          <div className="pcs2-setting-copy">
            <span className="pcs2-setting-title">Habilitar saudação</span>
          </div>
          <div className="pcs2-setting-actions">
            <VchPcs2Switch
              checked={greetingEnabled}
              onChange={(e) => setGreetingEnabled(e.target.checked)}
              ariaLabel="Habilitar saudação"
            />
          </div>
        </div>
        {greetingEnabled ? (
          <textarea
            className="vch-textarea"
            rows={3}
            placeholder="Olá! Como podemos ajudar?"
            value={greetingMessage}
            onChange={(e) => setGreetingMessage(e.target.value)}
          />
        ) : null}
          </VchSection>

          <hr className="pcs2-divider" />

          <VchSection
            icon={UsersRound}
            title="Agrupar conversas por cliente"
            desc="Na lista do chat (pré-atendimento e setores), agrupa as conversas pelo cliente do portal."
          >
            <div className="pcs2-setting-row">
              <div className="pcs2-setting-copy">
                <span className="pcs2-setting-title">Habilitar agrupamento por cliente</span>
              </div>
              <div className="pcs2-setting-actions">
                <VchPcs2Switch
                  checked={groupConversationsByClient}
                  onChange={(e) => setGroupConversationsByClient(e.target.checked)}
                  ariaLabel="Habilitar agrupamento por cliente"
                />
              </div>
            </div>
          </VchSection>

          <hr className="pcs2-divider" />

          <VchSection
            icon={Inbox}
            title="Horário de atendimento"
            desc="Fora do horário, a mensagem automática abaixo é exibida."
          >
        <div className="pcs2-setting-row">
          <div className="pcs2-setting-copy">
            <span className="pcs2-setting-title">Habilitar horário de atendimento</span>
          </div>
          <div className="pcs2-setting-actions">
            <VchPcs2Switch
              checked={workingHoursEnabled}
              onChange={(e) => setWorkingHoursEnabled(e.target.checked)}
              ariaLabel="Habilitar horário"
            />
          </div>
        </div>
        {workingHoursEnabled ? (
          <span
            className="vch-textarea-tooltip-host ci2-modern-tooltip-host"
            data-tooltip="Mensagem predefinida para quando estiver fora do horário"
          >
            <textarea
              className="vch-textarea"
              rows={2}
              placeholder="Estamos fora do horário de atendimento."
              value={outOfOfficeMessage}
              onChange={(e) => setOutOfOfficeMessage(e.target.value)}
            />
          </span>
        ) : null}
        <div className="vch-hours-grid">
          {workingHours.map((w) => {
            const openValue = `${String(w.open_hour ?? 9).padStart(2, '0')}:${String(
              w.open_minutes ?? 0
            ).padStart(2, '0')}`;
            const closeValue = `${String(w.close_hour ?? 18).padStart(2, '0')}:${String(
              w.close_minutes ?? 0
            ).padStart(2, '0')}`;
            return (
              <div
                key={w.day_of_week}
                className={`vch-hours-row${w.closed_all_day ? ' is-closed' : ''}`}
              >
                <span className="vch-hours-day">{DAY_LABELS[w.day_of_week]}</span>
                <VchPcs2Switch
                  className="vch-hours-switch"
                  checked={!w.closed_all_day}
                  disabled={!workingHoursEnabled}
                  ariaLabel={`${w.closed_all_day ? 'Abrir' : 'Fechar'} ${DAY_LABELS[w.day_of_week]}`}
                  onChange={(e) =>
                    updateDay(w.day_of_week, { closed_all_day: !e.target.checked })
                  }
                />
                <span
                  className={`vch-hours-state${w.closed_all_day ? ' is-closed' : ''}`}
                >
                  {w.closed_all_day ? 'Fechado' : 'Aberto'}
                </span>
                {!w.closed_all_day ? (
                  <div className="vch-hours-times">
                    <span className="vch-hours-times-label">das</span>
                    <input
                      type="time"
                      className="vch-hours-time-input"
                      disabled={!workingHoursEnabled}
                      value={openValue}
                      onChange={(e) => {
                        const [h, m] = String(e.target.value || '09:00')
                          .split(':')
                          .map(Number);
                        updateDay(w.day_of_week, {
                          open_hour: Number.isFinite(h) ? h : 9,
                          open_minutes: Number.isFinite(m) ? m : 0,
                        });
                      }}
                      aria-label={`Abertura ${DAY_LABELS[w.day_of_week]}`}
                    />
                    <span className="vch-hours-times-label">até</span>
                    <input
                      type="time"
                      className="vch-hours-time-input"
                      disabled={!workingHoursEnabled}
                      value={closeValue}
                      onChange={(e) => {
                        const [h, m] = String(e.target.value || '18:00')
                          .split(':')
                          .map(Number);
                        updateDay(w.day_of_week, {
                          close_hour: Number.isFinite(h) ? h : 18,
                          close_minutes: Number.isFinite(m) ? m : 0,
                        });
                      }}
                      aria-label={`Fechamento ${DAY_LABELS[w.day_of_week]}`}
                    />
                  </div>
                ) : (
                  <span className="vch-hours-closed-note">Fechado o dia todo</span>
                )}
              </div>
            );
          })}
        </div>
        <div className="pcs2-setting-row vch-block-outside-row">
          <div className="pcs2-setting-copy">
            <span className="pcs2-setting-title">Fechar chat fora do horário</span>
            <span className="pcs2-setting-desc">
              Quando ativo, o cliente não consegue digitar fora do horário de atendimento.
            </span>
          </div>
          <div className="pcs2-setting-actions">
            <VchPcs2Switch
              checked={blockOutsideHours}
              disabled={!workingHoursEnabled}
              onChange={(e) => setBlockOutsideHours(e.target.checked)}
              ariaLabel="Fechar chat fora do horário"
            />
          </div>
        </div>
          </VchSection>
        </div>
      </div>

      <div className="vch-save-bar">
        <button
          type="button"
          className="vch-inbox-save-btn"
          onClick={handleSave}
          disabled={saving || !dirty}
        >
          Salvar inbox
        </button>
      </div>
    </div>
  );
}

const SECTION_ICONS = {
  'agents-teams': Network,
  agents: Users,
  teams: UsersRound,
  inbox: Inbox,
};

const VISIBLE_CHAT_SECTIONS = CHAT_SECTIONS.filter((s) => !['agents', 'teams'].includes(s.id));

export default function CustomersChatView({ embedded = false }) {
  const [section, setSection] = useState('agents-teams');
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);

  const adminOk = canUseChatAdmin(settings?.chatwootStatus, settings?.chatEnabled);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSettingsLoading(true);
      try {
        const j = await fetchChatSettings();
        if (!cancelled) setSettings(j.data || null);
      } catch {
        if (!cancelled) setSettings(null);
      } finally {
        if (!cancelled) setSettingsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!VISIBLE_CHAT_SECTIONS.some((s) => s.id === section)) {
      setSection('agents-teams');
    }
  }, [section]);

  if (settingsLoading) {
    return (
      <div className={`vch-page${embedded ? ' vch-page--embedded' : ''}`}>
        <ProcessConfigSkeleton variant="settings" ariaLabel="Carregando chat" />
      </div>
    );
  }

  if (!adminOk) {
    const hint =
      settings?.chatwootStatus !== 'active'
        ? chatStatusHint(settings?.chatwootStatus)
        : 'Habilite o chat em Usuários > editar cliente/grupo > Acesso ao Chat.';
    return (
      <div className={`vch-page${embedded ? ' vch-page--embedded' : ''}`}>
        <div className="vch-gate">
          <IconBrandHipchat size={28} stroke={1.5} aria-hidden />
          <h2 className="vch-gate-title">Chat indisponível</h2>
          <p className="vch-gate-desc">{hint}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`vch-page${embedded ? ' vch-page--embedded' : ''}`}>
      <div className="vch-shell">
        <aside className="vch-sidebar">
          <div className="vch-sidebar-head">
            <h1 className="vch-sidebar-title">Chat</h1>
            <p className="vch-sidebar-sub">Agentes, filas e mensagens automáticas</p>
          </div>
          <nav className="vch-nav">
            {VISIBLE_CHAT_SECTIONS.map((s) => {
              const Icon = SECTION_ICONS[s.id] || Users;
              return (
                <button
                  key={s.id}
                  type="button"
                  className={`vch-nav-item${section === s.id ? ' active' : ''}`}
                  onClick={() => setSection(s.id)}
                >
                  <Icon size={15} strokeWidth={2} aria-hidden />
                  <span>{s.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        <div className="vch-main">
          {section === 'inbox' ? (
            <InboxPanel enabled={adminOk} />
          ) : (
            <div className="vch-main-scroll">
              {section === 'agents-teams' && (
                <CustomersChatAgentsTeamsPanel enabled={adminOk} />
              )}
              {section === 'agents' && <AgentsPanel enabled={adminOk} />}
              {section === 'teams' && (
                <TeamsPanel enabled={adminOk} clientId={settings?.clientId} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
